import Stack from "@mui/material/Stack";
import { Switches } from "mui-rff";
import { useMemo } from "react";
import { createListCollection } from "@ark-ui/react";
import { useField, useForm } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import { FormValues } from "@/types/widget";
import Select from "@/components/select/select";
import { OpmetReportWidgetConfiguration } from "@/lib/api";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";

enum AirportsType {
  EHAM = "EHAM",
  EHAA = "EHAA",
  EHRD = "EHRD",
  EHLE = "EHLE",
  EHGG = "EHGG",
  EHBK = "EHBK",
  EHEH = "EHEH",
  EHKD = "EHKD",
  EHDL = "EHDL",
  EHGR = "EHGR",
  EHLW = "EHLW",
  EHVK = "EHVK",
  EHWO = "EHWO",
  EHVL = "EHVL",
  TNCE = "TNCE",
  TNCB = "TNCB",
}

enum WeatherMessageType {
  METAR = "metar",
  TAF = "taf",
  SIGMET = "sigmet",
  AIRMET = "airmet",
  CYCLOON_WARNING = "warning_cycloon",
  VOLCANIC_ASH_WARNING = "warning_volcanic_ash",
  ALL = "all",
}

const DEFAULT_VALUES: OpmetReportWidgetConfiguration = {
  airport: "",
  include_airport_name: true,
  report_type: WeatherMessageType.ALL,
  type: "opmet_report",
  inputs: { location: false, time_range: false },
};

const getInitialValues = (values: FormValues) => {
  const configuration = values?.configuration;
  if (!configuration) return DEFAULT_VALUES;

  const initialValues = DEFAULT_VALUES;

  if ("airport" in configuration) {
    initialValues.airport = configuration.airport;
  }

  if ("include_airport_name" in configuration) {
    initialValues.include_airport_name = configuration.include_airport_name;
  }

  if ("report_type" in configuration) {
    initialValues.report_type = configuration.report_type;
  }

  return configuration as OpmetReportWidgetConfiguration;
};

export function OpmetReportWidgetForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const initialValues = getInitialValues(values);

  const airportTypeCollection = useMemo(() => {
    return createListCollection({
      items: Object.keys(AirportsType).map((key) => ({
        label: AirportsType[key as keyof typeof AirportsType],
        value: key,
      })),
    });
  }, []);
  const weatherMessageTypeCollection = useMemo(() => {
    return createListCollection({
      items: Object.entries(WeatherMessageType).map(([key, value]) => ({
        label: t(
          `admin.widgets.fields.opmet.report_type.${key as keyof typeof WeatherMessageType}`,
        ),
        value,
      })),
    });
  }, []);

  const form = useForm();
  const airportField = useField("configuration.airport", {
    defaultValue: initialValues.airport,
  });
  const reportTypeField = useField("configuration.report_type", {
    initialValue:
      (initialValues.report_type as WeatherMessageType) ?? WeatherMessageType.ALL,
  });

  return (
    <Stack mt={2}>
      <Select
        label={t("admin.widgets.fields.opmet.report.airport")}
        collection={airportTypeCollection}
        name={airportField.input.name}
        value={airportField.input.value}
        onValueChange={(value) => form.change(airportField.input.name, value)}
        error={airportField.input.touched && airportField.meta.error}
      />
      <Switches
        name={"configuration.include_airport_name"}
        data={{
          label: t("admin.widgets.fields.opmet.report.include_airport_name"),
          value: true,
        }}
        required
      />
      <Select
        label={t("admin.widgets.fields.opmet.report.type")}
        collection={weatherMessageTypeCollection}
        name={reportTypeField.input.name}
        value={reportTypeField.input.value}
        onValueChange={(value) => form.change(reportTypeField.input.name, value)}
        error={reportTypeField.meta.touched && reportTypeField.meta.error}
      />
    </Stack>
  );
}
